/**
 * M10 P4b4b/P4b4c: the kit lookups the sim, the HUD, traffic, the raid hook and the dev tools read. No kit data is
 * imported here, so those readers stay in their own chunks.
 *
 * - **The slots:** `addKitSlots` (`kits.ts`) appends every (kit, base hull) def once, at boot, and records it
 *   here. `kitDefIndex` is a lookup and never appends; `kitBaseOf` names a slot's base.
 * - **The names:** `promote` sets a kitted ship's name per entity for the target readout; `despawnShip` clears it,
 *   so a reused entity id never shows a stale name.
 */
import type { ShipDef } from '../data/schemas'
import type { Sim } from '../sim'
import { MAX_ENTITIES } from '../world/components'

/** Per defs array: `<base index>:<kit id>` → kit slot, and each slot's base from `first` (the base def count) on. */
export const kitSlotRegistry = new WeakMap<readonly ShipDef[], { bySlot: Map<string, number>; first: number; base: number[] }>()

/** The `sim.defs` slot of kit `kit` on base slot `base` (made by `addKitSlots`), or -1 (none, or the kit does not fit). Rare (a spawn). */
export function kitDefIndex(sim: Pick<Sim, 'defs'>, base: number, kit: { readonly id: string }): number {
  return kitSlotRegistry.get(sim.defs)?.bySlot.get(`${base}:${kit.id}`) ?? -1
}

/** Kit slot `slot`'s base def index, or -1 when `slot` is not a kit slot. */
export function kitBaseOf(defs: readonly ShipDef[], slot: number): number {
  const reg = kitSlotRegistry.get(defs)
  return reg === undefined || slot < reg.first ? -1 : (reg.base[slot - reg.first] ?? -1)
}

const nameDef = new Int32Array(MAX_ENTITIES).fill(-1)
const nameOf: (string | null)[] = new Array<string | null>(MAX_ENTITIES).fill(null)

/** Records entity `eid`'s kit name while it flies def slot `defIndex` (null clears it). Rare. */
export function setKitName(eid: number, defIndex: number, name: string | null): void {
  nameDef[eid] = name === null ? -1 : defIndex
  nameOf[eid] = name
}

/** Entity `eid`'s kit name when it still flies the slot it was named on, else null. Allocation-free. */
export function kitNameOf(eid: number, defIndex: number): string | null {
  return eid >= 0 && eid < MAX_ENTITIES && nameDef[eid] === defIndex ? nameOf[eid] : null
}
